/**
 * Sprint V102 (Mo 21.09.2026) — Reactivation-Landing für lauwarme DOI-Kontakte.
 *
 * Route: /schneller-zugang?t=<signed-token>
 *
 * Wichtige UX-Prinzipien (Founder-Direktive):
 *  - KEIN „Hallo {Vorname}" — Massen-Mail liefert keine PII.
 *  - Aktiver Consent-Klick — Checkbox + Button, nicht nur Landung.
 *  - Volltransparent: Was passiert beim Klick? (Free-Account, keine Zahlung,
 *    jederzeit löschbar via /profil > Danger-Zone).
 *  - Kein Retargeting/Analytics vor Consent.
 */
import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  CheckCircle2, AlertCircle, ArrowRight, Loader2, Sparkles, ShieldCheck,
} from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function SchnellerZugang() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { setUser } = useAuth();

  const token = params.get("t") || "";
  const firstNameParam = params.get("fn") || "";
  const [tokenState, setTokenState] = useState({ status: "checking" });
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [emailInput, setEmailInput] = useState("");

  // Token beim Load prüfen
  useEffect(() => {
    if (!token) {
      setTokenState({ status: "missing" });
      return;
    }
    let cancelled = false;
    api.get(`/reactivation/verify?t=${encodeURIComponent(token)}&fn=${encodeURIComponent(firstNameParam)}`)
      .then((r) => {
        if (cancelled) return;
        if (r.data?.ok) {
          setTokenState({
            status: "valid",
            emailMasked: r.data.email_masked,
            firstName: r.data.first_name || firstNameParam || "",
          });
        } else {
          setTokenState({ status: "invalid", reason: r.data?.reason });
        }
      })
      .catch(() => { if (!cancelled) setTokenState({ status: "error" }); });
    return () => { cancelled = true; };
  }, [token, firstNameParam]);

  const handleActivate = async () => {
    if (!consent || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const { data } = await api.post("/reactivation/one-click-signup", {
        token,
        consent: true,
        fn: firstNameParam || undefined,
      });
      try { setUser?.(null); } catch { /* ignore */ }
      navigate(data?.redirect_to || "/dashboard", { replace: true });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const reason = typeof detail === "object" ? detail?.reason : detail;
      setError(
        reason === "expired" ? "Der Link ist leider abgelaufen. Bitte melde dich per E-Mail bei uns."
        : reason === "invalid_signature" ? "Der Link ist ungültig. Bitte nutze den Link aus der Original-Mail."
        : "Aktivierung fehlgeschlagen. Bitte erneut versuchen oder Support kontaktieren.",
      );
      setSubmitting(false);
    }
  };

  // Sprint V102.c — Fallback ohne Token: E-Mail-basierter Signup.
  const handleEmailActivate = async () => {
    if (!consent || submitting) return;
    const email = emailInput.trim().toLowerCase();
    if (!email || !email.includes("@") || email.length < 5) {
      setError("Bitte gib eine gültige E-Mail-Adresse ein.");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const { data } = await api.post("/reactivation/email-signup", {
        email, consent: true,
      });
      try { setUser?.(null); } catch { /* ignore */ }
      navigate(data?.redirect_to || "/dashboard", { replace: true });
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 404 && typeof detail === "object") {
        setError(detail.message || "Diese E-Mail-Adresse ist bei uns nicht hinterlegt.");
      } else if (status === 400) {
        setError("Bitte gib eine gültige E-Mail-Adresse ein.");
      } else {
        setError("Aktivierung fehlgeschlagen. Bitte erneut versuchen.");
      }
      setSubmitting(false);
    }
  };

  return (
    <div
      className="min-h-screen bg-gradient-to-br from-slate-50 to-orange-50 dark:from-slate-950 dark:to-slate-900"
      data-testid="schneller-zugang-page"
      data-token-state={tokenState.status}
    >
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-16 sm:py-24">
        {/* Sprint V102.e (Mo 21.09.2026, 20:00) — Personalisierte Anrede
            wenn KT-Personalisierung `%Subscriber:CustomFieldFirstName%`
            als `fn`-Query-Param uebergeben wurde. */}
        {tokenState.status === "valid" && tokenState.firstName && (
          <p
            className="text-center text-base sm:text-lg text-slate-600 dark:text-slate-300 mb-4"
            data-testid="schneller-zugang-greeting"
          >
            Hallo <b className="text-slate-900 dark:text-white">{tokenState.firstName}</b>,
          </p>
        )}

        {/* Headline */}
        <h1
          className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-slate-900 dark:text-white text-center leading-[1.1]"
          data-testid="schneller-zugang-headline"
        >
          Dein PocketCoach ist bereit.<br />
          <span className="text-[#F3651D]">Ein Klick reicht.</span>
        </h1>

        <p
          className="mt-6 text-base sm:text-lg text-slate-700 dark:text-slate-300 text-center leading-relaxed max-w-xl mx-auto"
          data-testid="schneller-zugang-subline"
        >
          Klinke dich <b>kostenfrei</b> in deinen PocketCoach ein — ohne Anmeldung, ohne neuen Aufwand. Deine E-Mail-Adresse ist ja bereits bei uns bestätigt.
        </p>

        {/* Token-Status-Handling */}
        {tokenState.status === "checking" && (
          <div className="mt-10 flex items-center justify-center gap-2 text-slate-600 dark:text-slate-300" data-testid="schneller-zugang-checking">
            <Loader2 className="w-4 h-4 animate-spin" /> Prüfe deinen Link …
          </div>
        )}

        {tokenState.status === "missing" && (
          <div
            className="mt-10 rounded-2xl bg-white dark:bg-slate-900 border-2 border-[#F3651D]/40 shadow-lg overflow-hidden"
            data-testid="schneller-zugang-email-fallback"
          >
            <div className="p-6 sm:p-8">
              <div className="flex items-center gap-2 mb-3">
                <ShieldCheck className="w-5 h-5 text-[#F3651D]" />
                <p className="text-xs font-bold uppercase tracking-widest text-[#F3651D]">
                  Kurz deine E-Mail — dann geht's los
                </p>
              </div>
              <p className="text-sm text-slate-600 dark:text-slate-300 mb-4">
                Gib bitte kurz die E-Mail-Adresse ein, an die Andreas dir die Mail geschickt hat. Ein Klick — und dein PocketCoach ist fertig eingerichtet.
              </p>

              <input
                type="email"
                value={emailInput}
                onChange={(e) => setEmailInput(e.target.value)}
                placeholder="deine@e-mail.de"
                autoComplete="email"
                data-testid="schneller-zugang-email-input"
                className="w-full rounded-xl border-2 border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-4 py-3 text-base text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#F3651D] focus:border-[#F3651D]"
              />

              <ul className="mt-5 space-y-2 text-sm text-slate-700 dark:text-slate-200">
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                  <span>Kostenfreier PocketCoach — keine Zahlung, kein Abo.</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                  <span>Jederzeit im Profil selbst löschbar.</span>
                </li>
              </ul>

              <label
                className="mt-5 flex items-start gap-3 cursor-pointer rounded-xl border border-slate-200 dark:border-slate-700 hover:border-[#F3651D]/50 bg-slate-50/50 dark:bg-slate-800/40 p-4"
                data-testid="schneller-zugang-email-consent-label"
              >
                <input
                  type="checkbox"
                  checked={consent}
                  onChange={(e) => setConsent(e.target.checked)}
                  data-testid="schneller-zugang-email-consent-checkbox"
                  className="mt-1 w-4 h-4 accent-[#F3651D] flex-shrink-0"
                />
                <span className="text-sm text-slate-800 dark:text-slate-100 leading-snug">
                  <b>Ja, richte meinen kostenfreien PocketCoach ein.</b> Ich habe verstanden, dass mir keine Kosten entstehen und ich das Konto jederzeit selbst löschen kann.
                </span>
              </label>

              <button
                type="button"
                onClick={handleEmailActivate}
                disabled={!consent || submitting || !emailInput.trim()}
                data-testid="schneller-zugang-email-activate-btn"
                className="mt-5 w-full inline-flex items-center justify-center gap-2 rounded-xl bg-[#F3651D] hover:bg-[#d95415] disabled:opacity-40 disabled:cursor-not-allowed text-white font-extrabold text-base sm:text-lg px-6 py-4 shadow-md transition-all"
              >
                {submitting ? (
                  <><Loader2 className="w-5 h-5 animate-spin" /> Richte PocketCoach ein …</>
                ) : (
                  <><Sparkles className="w-5 h-5" /> PocketCoach jetzt freischalten <ArrowRight className="w-5 h-5" /></>
                )}
              </button>

              {error && (
                <p className="mt-4 text-sm text-red-600 dark:text-red-400 font-semibold text-center" data-testid="schneller-zugang-email-error">
                  {error}
                </p>
              )}
            </div>

            <div className="px-6 sm:px-8 py-4 bg-slate-50 dark:bg-slate-800/60 border-t border-slate-200 dark:border-slate-700 text-xs text-slate-500 dark:text-slate-400">
              Datensparsam gebaut nach DSGVO. Kein Tracking, keine Cookies vor deinem Klick.{" "}
              <a href="/datenschutz" className="underline hover:text-slate-700 dark:hover:text-slate-200">Datenschutz</a>
              {" · "}
              <a href="/impressum" className="underline hover:text-slate-700 dark:hover:text-slate-200">Impressum</a>
            </div>
          </div>
        )}

        {tokenState.status === "invalid" && (
          <div className="mt-10 rounded-2xl border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-950/30 p-5" data-testid="schneller-zugang-token-invalid">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-bold text-red-900 dark:text-red-200">
                  {tokenState.reason === "expired" ? "Dein Link ist abgelaufen" : "Der Link ist nicht gültig"}
                </p>
                <p className="mt-1 text-sm text-red-800 dark:text-red-300">
                  {tokenState.reason === "expired"
                    ? "Der Aktivierungs-Link läuft nach 30 Tagen ab. Antworte auf die E-Mail von Andreas — wir schicken dir einen neuen Link."
                    : "Bitte nutze den Original-Link aus der E-Mail. Falls das Problem bleibt, antworte einfach auf die Mail."}
                </p>
              </div>
            </div>
          </div>
        )}

        {tokenState.status === "valid" && (
          <>
            {/* Consent-Karte */}
            <div
              className="mt-10 rounded-2xl bg-white dark:bg-slate-900 border-2 border-[#F3651D]/40 shadow-lg overflow-hidden"
              data-testid="schneller-zugang-consent-card"
            >
              <div className="p-6 sm:p-8">
                <div className="flex items-center gap-2 mb-4">
                  <ShieldCheck className="w-5 h-5 text-[#F3651D]" />
                  <p className="text-xs font-bold uppercase tracking-widest text-[#F3651D]">
                    Bevor's losgeht — kurz Klarheit
                  </p>
                </div>

                <p className="text-sm text-slate-600 dark:text-slate-400 mb-3">
                  Aktivieren für <span className="font-mono font-semibold text-slate-800 dark:text-slate-200" data-testid="schneller-zugang-email-masked">{tokenState.emailMasked}</span>
                </p>

                <ul className="space-y-3 text-sm text-slate-700 dark:text-slate-200">
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                    <span>Wir legen für dich einen <b>kostenfreien Free-Account</b> in unserem Mitgliederbereich an.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                    <span><b>Keine Zahlung, kein Abo</b> — jetzt und nicht später automatisch.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                    <span>Du kannst das Konto <b>jederzeit selbst wieder löschen</b> — im Profil unter „Konto & Daten".</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                    <span>Deine E-Mail-Adresse ist bereits bestätigt — es wird <b>keine weitere Consent-Mail</b> versendet.</span>
                  </li>
                </ul>

                {/* Consent-Checkbox */}
                <label
                  className="mt-6 flex items-start gap-3 cursor-pointer rounded-xl border border-slate-200 dark:border-slate-700 hover:border-[#F3651D]/50 bg-slate-50/50 dark:bg-slate-800/40 p-4"
                  data-testid="schneller-zugang-consent-label"
                >
                  <input
                    type="checkbox"
                    checked={consent}
                    onChange={(e) => setConsent(e.target.checked)}
                    data-testid="schneller-zugang-consent-checkbox"
                    className="mt-1 w-4 h-4 accent-[#F3651D] flex-shrink-0"
                  />
                  <span className="text-sm text-slate-800 dark:text-slate-100 leading-snug">
                    <b>Ja, richte meinen kostenfreien PocketCoach ein.</b> Ich habe verstanden, dass mir keine Kosten entstehen und ich das Konto jederzeit selbst löschen kann.
                  </span>
                </label>

                {/* Primary CTA */}
                <button
                  type="button"
                  onClick={handleActivate}
                  disabled={!consent || submitting}
                  data-testid="schneller-zugang-activate-btn"
                  className="mt-5 w-full inline-flex items-center justify-center gap-2 rounded-xl bg-[#F3651D] hover:bg-[#d95415] disabled:opacity-40 disabled:cursor-not-allowed text-white font-extrabold text-base sm:text-lg px-6 py-4 shadow-md transition-all"
                >
                  {submitting ? (
                    <><Loader2 className="w-5 h-5 animate-spin" /> Richte PocketCoach ein …</>
                  ) : (
                    <><Sparkles className="w-5 h-5" /> PocketCoach jetzt freischalten <ArrowRight className="w-5 h-5" /></>
                  )}
                </button>

                {error && (
                  <p className="mt-4 text-sm text-red-600 dark:text-red-400 font-semibold text-center" data-testid="schneller-zugang-error">
                    {error}
                  </p>
                )}
              </div>

              <div className="px-6 sm:px-8 py-4 bg-slate-50 dark:bg-slate-800/60 border-t border-slate-200 dark:border-slate-700 text-xs text-slate-500 dark:text-slate-400">
                Datensparsam gebaut nach DSGVO. Kein Tracking, keine Cookies vor deinem Klick.{" "}
                <a href="/datenschutz" className="underline hover:text-slate-700 dark:hover:text-slate-200">Datenschutz</a>
                {" · "}
                <a href="/impressum" className="underline hover:text-slate-700 dark:hover:text-slate-200">Impressum</a>
              </div>
            </div>

            {/* P.S. Webinar-Hint */}
            <div className="mt-8 rounded-xl bg-slate-100/60 dark:bg-slate-800/40 p-5 text-center" data-testid="schneller-zugang-webinar-hint">
              <p className="text-xs font-bold uppercase tracking-widest text-[#F3651D]">P.S. Live-Webinar</p>
              <p className="mt-1 text-sm text-slate-700 dark:text-slate-300">
                Freitag, 25.09. · ca. 10 Uhr — ich zeige dir live, wie das mit deinen eigenen Beispielen arbeitet.
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
